(() => {
	const YOUTUBE_DOMAINS = ["youtube.com", "youtu.be", "www.youtube.com", "m.youtube.com"];
	const VIMEO_DOMAINS = ["vimeo.com", "player.vimeo.com", "www.vimeo.com"];

	/** @param {string} raw */
	const safeUrl = (raw) => {
		if (!raw) return "";

		try {
			const url = new URL(raw.trim());
			return url.protocol === "https:" ? url : null;
		} catch (_error) {
			return null;
		}
	};

	/** @param {string} rawUrl */
	const toEmbedUrl = (rawUrl) => {
		const url = safeUrl(rawUrl);
		if (!url) return "";

		if (YOUTUBE_DOMAINS.includes(url.hostname)) {
			if (url.hostname === "youtu.be") {
				const id = url.pathname.replace("/", "");
				return id ? `https://www.youtube.com/embed/${id}` : "";
			}

			if (url.pathname.startsWith("/embed/")) {
				return url.toString();
			}

			const videoId = url.searchParams.get("v");
			return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
		}

		if (VIMEO_DOMAINS.includes(url.hostname)) {
			if (url.hostname === "player.vimeo.com") {
				return url.toString();
			}

			const videoId = url.pathname.split("/").filter(Boolean).pop();
			return videoId ? `https://player.vimeo.com/video/${videoId}` : "";
		}

		return "";
	};

	/** @param {string} embedUrl */
	const getYoutubeThumbnail = (embedUrl) => {
		try {
			const url = new URL(embedUrl);
			if (url.hostname === "www.youtube.com" && url.pathname.startsWith("/embed/")) {
				const videoId = url.pathname.split("/embed/")[1].split("?")[0];
				return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "";
			}
		} catch (_) {}
		return "";
	};

	/**
	 * @param {string} src
	 * @param {string} title
	 * @param {string} iframeFallback
	 */
	const createIframe = (src, title, iframeFallback) => {
		const iframe = document.createElement("iframe");
		iframe.src = `${src}${src.includes("?") ? "&" : "?"}rel=0&modestbranding=1`;
		iframe.title = title || iframeFallback;
		iframe.loading = "lazy";
		iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
		iframe.referrerPolicy = "strict-origin-when-cross-origin";
		iframe.allowFullscreen = true;
		return iframe;
	};

	/**
	 * @param {HTMLElement} container
	 * @param {string} embedSrc
	 * @param {string} title
	 * @param {string} playIconUrl
	 * @param {{ iframeFallback: string; thumbAlt: string; playPrefix: string }} a11y
	 */
	const createThumbnailPlayer = (container, embedSrc, title, playIconUrl = "", a11y) => {
		const iframeFallback = a11y?.iframeFallback || "Tutorial video";
		const thumbAlt = a11y?.thumbAlt || "Video thumbnail";
		const playPrefix = a11y?.playPrefix || "Play:";
		const labelTitle = title || iframeFallback;

		const thumbUrl = getYoutubeThumbnail(embedSrc);

		const img = document.createElement("img");
		img.src = thumbUrl || "";
		img.alt = title ? `${title}` : thumbAlt;
		img.style.cssText = "width:100%;height:100%;object-fit:cover;display:block;";
		if (!thumbUrl) img.hidden = true;

		const playBtn = document.createElement("button");
		playBtn.type = "button";
		playBtn.setAttribute("aria-label", `${playPrefix} ${labelTitle}`.trim());
		playBtn.style.cssText = "position:absolute;inset:0;width:100%;height:100%;background:rgba(0,0,0,0.3);border:0;cursor:pointer;display:flex;align-items:center;justify-content:center;";
		if (playIconUrl) {
			const iconImg = document.createElement("img");
			iconImg.src = playIconUrl;
			iconImg.alt = "";
			iconImg.setAttribute("aria-hidden", "true");
			iconImg.style.cssText = "width:20px;height:20px;pointer-events:none;";
			playBtn.appendChild(iconImg);
		} else {
			playBtn.innerHTML = `<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="28" cy="28" r="28" fill="rgba(0,0,0,0.6)"/><polygon points="22,18 42,28 22,38" fill="white"/></svg>`;
		}

		playBtn.addEventListener("click", () => {
			const autoplaySrc = `${embedSrc}${embedSrc.includes("?") ? "&" : "?"}autoplay=1&rel=0&modestbranding=1`;
			const iframe = document.createElement("iframe");
			iframe.src = autoplaySrc;
			iframe.title = labelTitle;
			iframe.loading = "lazy";
			iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
			iframe.referrerPolicy = "strict-origin-when-cross-origin";
			iframe.allowFullscreen = true;
			iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:0;";
			container.innerHTML = "";
			container.style.position = "relative";
			container.appendChild(iframe);
		});

		container.style.position = "relative";
		container.appendChild(img);
		container.appendChild(playBtn);
	};

	/** @param {HTMLElement[]} items
	 *  @param {HTMLElement | null} current
	 */
	const setActiveItem = (items, current) => {
		items.forEach((item) => {
			const isActive = item === current;
			item.classList.toggle("is-active", isActive);
			item.hidden = isActive;
			item.setAttribute("aria-hidden", isActive ? "true" : "false");
			item.setAttribute("tabindex", isActive ? "-1" : "0");
			item.setAttribute("aria-pressed", isActive ? "true" : "false");
		});
	};

	const initTutorials = () => {
		const roots = document.querySelectorAll("[data-planner-video-root]");

		roots.forEach((root) => {
			const frame = /** @type {HTMLElement | null} */ (root.querySelector("[data-planner-video-frame]"));
			const placeholder = /** @type {HTMLElement | null} */ (root.querySelector("[data-planner-video-placeholder]"));
			const titleEl = /** @type {HTMLElement | null} */ (root.querySelector("[data-planner-video-title]"));
			const descriptionEl = /** @type {HTMLElement | null} */ (root.querySelector("[data-planner-video-description]"));
			const items = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll("[data-planner-video-item]")));
			const featuredVideoSrc = root.getAttribute("data-featured-video-src") || "";
			const featuredVideoTitle = root.getAttribute("data-featured-video-title") || "";
			const featuredVideoDescription = root.getAttribute("data-featured-video-description") || "";
			const playIconUrl = root.getAttribute("data-play-icon-url") || "";

			const a11y = {
				iframeFallback: root.dataset.plannerA11yIframeFallback?.trim() || "Tutorial video",
				thumbAlt: root.dataset.plannerA11yThumbAlt?.trim() || "Video thumbnail",
				playPrefix: root.dataset.plannerA11yPlayPrefix?.trim() || "Play:",
			};

			const isMobile = () => window.innerWidth <= 767;

			if (isMobile()) {
				items.forEach((item) => {
					const thumbFrame = /** @type {HTMLElement | null} */ (item.querySelector("[data-planner-video-thumb]"));
					const embedSrc = toEmbedUrl(item.dataset.videoSrc || "");
					if (!thumbFrame || !embedSrc) return;
					thumbFrame.innerHTML = "";
					createThumbnailPlayer(thumbFrame, embedSrc, item.dataset.title || "", playIconUrl, a11y);
				});
				return;
			}

			items.forEach((item) => {
				const thumbFrame = /** @type {HTMLElement | null} */ (item.querySelector("[data-planner-video-thumb]"));
				const embedSrc = toEmbedUrl(item.dataset.videoSrc || "");
				if (!thumbFrame || !embedSrc) return;
				const thumbUrl = getYoutubeThumbnail(embedSrc);
				if (!thumbUrl) return;
				thumbFrame.innerHTML = "";
				const img = document.createElement("img");
				img.src = thumbUrl;
				img.alt = item.dataset.title || a11y.thumbAlt;
				img.style.cssText = "width:100%;height:100%;object-fit:cover;display:block;";
				thumbFrame.appendChild(img);
			});

			if (!frame) return;

			/** @param {HTMLElement} item */
			const activate = (item) => {
				const embedSrc = toEmbedUrl(item.dataset.videoSrc || "");
				if (!embedSrc) return;

				const itemTitle = item.dataset.title || "";
				const itemDescription = item.dataset.description || "";

				frame.querySelectorAll("iframe").forEach((el) => el.remove());
				frame.appendChild(createIframe(embedSrc, itemTitle, a11y.iframeFallback));

				if (placeholder) {
					placeholder.style.display = "none";
				}

				if (titleEl) {
					titleEl.textContent = itemTitle;
				}

				if (descriptionEl) {
					descriptionEl.textContent = itemDescription;
				}

				setActiveItem(items, item);
			};

			const activateFeaturedDefault = () => {
				const embedSrc = toEmbedUrl(featuredVideoSrc);
				if (!embedSrc) return;

				frame.querySelectorAll("iframe").forEach((el) => el.remove());
				frame.appendChild(createIframe(embedSrc, featuredVideoTitle, a11y.iframeFallback));

				if (placeholder) {
					placeholder.style.display = "none";
				}

				if (titleEl && featuredVideoTitle) {
					titleEl.textContent = featuredVideoTitle;
				}

				if (descriptionEl && featuredVideoDescription) {
					descriptionEl.textContent = featuredVideoDescription;
				}

				const featuredItem = items.find((i) => toEmbedUrl(i.dataset.videoSrc || "") === embedSrc);
				setActiveItem(items, featuredItem || null);
			};

			items.forEach((item) => {
				item.addEventListener("click", () => activate(item));
				item.addEventListener("keydown", (event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						activate(item);
					}
				});
			});

			const initialActive = items.find((item) => item.classList.contains("is-active"));
			if (initialActive) {
				activate(initialActive);
				return;
			}

			activateFeaturedDefault();
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initTutorials);
	} else {
		initTutorials();
	}
})();
